import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { useResolvedMedia } from '../hooks/useResolvedMedia'
import type { HeroImageFocus, HeroImageIntensity, HeroImageMotion, HeroImageSpeed, HeroImageVerticalFocus, HeroTextTone } from '../types'

export type PublicHeroSlide = {
  id: string
  kind: 'image' | 'video'
  desktopSrc: string
  mobileSrc?: string
  label?: string
  motion?: HeroImageMotion
  motionSpeed?: HeroImageSpeed
  motionIntensity?: HeroImageIntensity
  focus?: HeroImageFocus
  focusY?: HeroImageVerticalFocus
  textTone?: HeroTextTone
}

type HeroSlideshowProps = {
  slides?: PublicHeroSlide[]
  fallbackImage: string
  legacyDesktopVideo?: string
  legacyMobileVideo?: string
  isMobile: boolean
  prefersReducedMotion: boolean
  slideDurationSeconds?: number
  onActiveMediaBrightnessChange?: (isBright: boolean) => void
  onInitialMediaReady?: () => void
}

type HeroSlideLayerProps = {
  slide: PublicHeroSlide
  fallbackImage: string
  isMobile: boolean
  slideDurationMs: number
  initial: boolean
  active: boolean
  previous: boolean
  shouldLoad: boolean
  canPlay: boolean
  motionIndex: number
  onToneSample: (isBright: boolean, immediate: boolean) => void
  onToneReady: (mediaKey: string) => void
  onMediaReady: (mediaKey: string) => void
}

const AUTO_IMAGE_MOTIONS: HeroImageMotion[] = ['cinematic-zoom', 'drift-left', 'drift-right', 'gentle-pullback']
const IMAGE_MOTION_DURATION_MS: Record<HeroImageSpeed, number> = {
  gentle: 14_000,
  slow: 20_000,
  'very-slow': 28_000,
}

function sampleMediaBrightness(
  element: HTMLImageElement | HTMLVideoElement,
  isMobile: boolean,
  focus: HeroImageFocus,
  focusY: HeroImageVerticalFocus,
) {
  const sourceWidth = element instanceof HTMLVideoElement ? element.videoWidth : element.naturalWidth
  const sourceHeight = element instanceof HTMLVideoElement ? element.videoHeight : element.naturalHeight
  if (!sourceWidth || !sourceHeight) return null

  const width = isMobile ? 18 : 32
  const height = isMobile ? 30 : 18
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const context = canvas.getContext('2d', { willReadFrequently: true })
  if (!context) return null

  const targetAspect = width / height
  const sourceAspect = sourceWidth / sourceHeight
  let sourceX = 0
  let sourceY = 0
  let cropWidth = sourceWidth
  let cropHeight = sourceHeight
  const focusRatio = focus === 'left' ? 0.3 : focus === 'right' ? 0.7 : 0.5
  const focusYRatio = focusY === 'top' ? 0.25 : focusY === 'bottom' ? 0.75 : 0.5

  if (sourceAspect > targetAspect) {
    cropWidth = sourceHeight * targetAspect
    sourceX = (sourceWidth - cropWidth) * focusRatio
  } else {
    cropHeight = sourceWidth / targetAspect
    sourceY = (sourceHeight - cropHeight) * focusYRatio
  }

  try {
    context.drawImage(element, sourceX, sourceY, cropWidth, cropHeight, 0, 0, width, height)
    const pixels = context.getImageData(0, 0, width, height).data
    const sampleWidth = isMobile ? width : Math.round(width * 0.78)
    const sampleTop = Math.round(height * 0.1)
    const sampleBottom = Math.round(height * 0.9)
    let luminanceTotal = 0
    let brightPixels = 0
    let pixelCount = 0

    for (let y = sampleTop; y < sampleBottom; y += 1) {
      for (let x = 0; x < sampleWidth; x += 1) {
        const offset = (y * width + x) * 4
        if (pixels[offset + 3] < 96) continue
        const luminance = pixels[offset] * 0.2126 + pixels[offset + 1] * 0.7152 + pixels[offset + 2] * 0.0722
        luminanceTotal += luminance
        if (luminance >= 175) brightPixels += 1
        pixelCount += 1
      }
    }

    if (!pixelCount) return null
    const average = luminanceTotal / pixelCount
    const brightRatio = brightPixels / pixelCount
    return average >= 152 || (average >= 138 && brightRatio >= 0.58)
  } catch {
    return null
  }
}

function HeroSlideLayer({
  slide,
  fallbackImage,
  isMobile,
  slideDurationMs,
  initial,
  active,
  previous,
  shouldLoad,
  canPlay,
  motionIndex,
  onToneSample,
  onToneReady,
  onMediaReady,
}: HeroSlideLayerProps) {
  const reference = isMobile ? slide.mobileSrc || slide.desktopSrc : slide.desktopSrc || slide.mobileSrc || ''
  const mediaKey = `${slide.id}|${reference}`
  const { url } = useResolvedMedia(reference, shouldLoad)
  const imageRef = useRef<HTMLImageElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const reportedForActivationRef = useRef(false)
  const [readyImageUrl, setReadyImageUrl] = useState('')
  const [readyVideoUrl, setReadyVideoUrl] = useState('')
  const imageReady = Boolean(url && readyImageUrl === url)
  const videoReady = Boolean(url && readyVideoUrl === url)
  const mediaReady = slide.kind === 'image' ? imageReady : videoReady
  const imageMotion = slide.motion || AUTO_IMAGE_MOTIONS[motionIndex % AUTO_IMAGE_MOTIONS.length]
  const imageSpeed = slide.motionSpeed || 'slow'
  const imageFocus = slide.focus || 'center'
  const imageFocusY = slide.focusY || 'center'
  const configuredMotionDurationMs = IMAGE_MOTION_DURATION_MS[imageSpeed]
  const imageMotionDurationMs = isMobile
    ? Math.min(configuredMotionDurationMs, Math.max(6_200, slideDurationMs - 1_400))
    : configuredMotionDurationMs
  const layerStyle = slide.kind === 'image'
    ? ({ '--hero-image-motion-duration': `${imageMotionDurationMs}ms` } as CSSProperties)
    : undefined

  const reportElementTone = useCallback((element: HTMLImageElement | HTMLVideoElement) => {
    if (!active || (slide.textTone && slide.textTone !== 'auto')) return
    const isBright = sampleMediaBrightness(element, isMobile, imageFocus, imageFocusY)
    if (isBright === null) return
    onToneSample(isBright, !reportedForActivationRef.current)
    onToneReady(mediaKey)
    reportedForActivationRef.current = true
  }, [active, imageFocus, imageFocusY, isMobile, mediaKey, onToneReady, onToneSample, slide.textTone])

  useEffect(() => {
    if (!active) {
      reportedForActivationRef.current = false
      return
    }

    if (slide.textTone === 'dark' || slide.textTone === 'light') {
      onToneSample(slide.textTone === 'dark', true)
      onToneReady(mediaKey)
      reportedForActivationRef.current = true
      return
    }

    const sample = () => {
      const element = slide.kind === 'video' ? videoRef.current : imageRef.current
      if (element) reportElementTone(element)
    }
    const initialTimer = window.setTimeout(sample, 120)
    const fallbackTimer = window.setTimeout(() => {
      if (reportedForActivationRef.current) return
      onToneSample(false, true)
      onToneReady(mediaKey)
      reportedForActivationRef.current = true
    }, 1200)
    const interval = slide.kind === 'video' ? window.setInterval(sample, 1600) : undefined
    return () => {
      window.clearTimeout(initialTimer)
      window.clearTimeout(fallbackTimer)
      if (interval) window.clearInterval(interval)
    }
  }, [active, mediaKey, onToneReady, onToneSample, reportElementTone, slide.kind, slide.textTone, url])

  useEffect(() => {
    if (slide.kind !== 'video') return
    const video = videoRef.current
    if (!video) return

    if (!active || !canPlay || !url) {
      video.pause()
      return
    }

    const attemptPlayback = () => {
      const playback = video.play()
      if (playback) void playback.catch(() => undefined)
    }

    attemptPlayback()
  }, [active, canPlay, slide.kind, url])

  useEffect(() => {
    if (mediaReady) onMediaReady(mediaKey)
  }, [mediaKey, mediaReady, onMediaReady])

  const layerClassName = [
    'hero-slideshow__slide',
    `hero-slideshow__slide--${slide.kind}`,
    initial ? 'is-initial' : '',
    active ? 'is-active' : '',
    previous ? 'is-previous' : '',
    mediaReady ? 'is-ready' : '',
  ].filter(Boolean).join(' ')

  const retryPlayback = () => {
    setReadyVideoUrl(url)
    if (videoRef.current) reportElementTone(videoRef.current)
    if (!active || !canPlay) return
    const playback = videoRef.current?.play()
    if (playback) void playback.catch(() => undefined)
  }

  return (
    <div
      className={layerClassName}
      data-motion={slide.kind === 'image' ? imageMotion : undefined}
      data-speed={slide.kind === 'image' ? imageSpeed : undefined}
      data-intensity={slide.kind === 'image' ? slide.motionIntensity || 'balanced' : undefined}
      data-focus={slide.kind === 'image' ? imageFocus : undefined}
      data-focus-y={slide.kind === 'image' ? imageFocusY : undefined}
      data-mobile-sweep={slide.kind === 'image' && isMobile ? 'true' : undefined}
      style={layerStyle}
      aria-hidden={!active}
    >
      {slide.kind === 'image' && url && (
        <img
          ref={imageRef}
          src={url}
          alt={active ? slide.label || '' : ''}
          draggable={false}
          onLoad={(event) => {
            setReadyImageUrl(url)
            reportElementTone(event.currentTarget)
          }}
        />
      )}
      {slide.kind === 'video' && (
        <>
          <img className="hero-slideshow__poster" src={fallbackImage} alt="" draggable={false} />
          {url && (
            <video
              ref={videoRef}
              src={url}
              poster={fallbackImage}
              autoPlay={active && canPlay}
              muted
              loop
              playsInline
              preload={active ? 'auto' : 'metadata'}
              tabIndex={-1}
              aria-hidden="true"
              onCanPlay={retryPlayback}
              onLoadedData={(event) => {
                setReadyVideoUrl(url)
                reportElementTone(event.currentTarget)
              }}
            />
          )}
        </>
      )}
    </div>
  )
}

function cleanSlides(slides: PublicHeroSlide[] | undefined) {
  if (!Array.isArray(slides)) return []
  return slides
    .filter((slide): slide is PublicHeroSlide => (
      Boolean(slide?.id)
      && (slide?.kind === 'image' || slide?.kind === 'video')
      && Boolean(slide?.desktopSrc || slide?.mobileSrc)
    ))
    .slice(0, 10)
}

export function HeroSlideshow({
  slides,
  fallbackImage,
  legacyDesktopVideo = '',
  legacyMobileVideo = '',
  isMobile,
  prefersReducedMotion,
  slideDurationSeconds = 12,
  onActiveMediaBrightnessChange,
  onInitialMediaReady,
}: HeroSlideshowProps) {
  const slideDurationMs = Math.round(Math.min(20, Math.max(8, Number(slideDurationSeconds) || 12)) * 1000)
  const preparedSlides = useMemo(() => {
    const configuredSlides = cleanSlides(slides)

    if (prefersReducedMotion) {
      const stillSlide = configuredSlides.find((slide) => slide.kind === 'image')
      return [stillSlide ? { ...stillSlide, motion: 'still' as const } : {
        id: 'reduced-motion-fallback',
        kind: 'image' as const,
        desktopSrc: fallbackImage,
        label: 'Modern mountain home framed by trees',
        motion: 'still' as const,
        motionSpeed: 'slow' as const,
        motionIntensity: 'balanced' as const,
        focus: 'center' as const,
        focusY: 'center' as const,
      }]
    }

    if (configuredSlides.length > 0) return configuredSlides

    if (legacyDesktopVideo || legacyMobileVideo) {
      return [{
        id: 'legacy-hero-video',
        kind: 'video' as const,
        desktopSrc: legacyDesktopVideo || legacyMobileVideo,
        mobileSrc: legacyMobileVideo || legacyDesktopVideo,
        label: 'BlackRock Developers residence',
      }]
    }

    return [{
      id: 'legacy-hero-image',
      kind: 'image' as const,
      desktopSrc: fallbackImage,
      label: 'Modern mountain home framed by trees',
      motion: 'cinematic-zoom' as const,
      motionSpeed: 'slow' as const,
      motionIntensity: 'balanced' as const,
      focus: 'center' as const,
      focusY: 'center' as const,
    }]
  }, [fallbackImage, legacyDesktopVideo, legacyMobileVideo, prefersReducedMotion, slides])

  const [activeIndex, setActiveIndex] = useState(0)
  const [previousIndex, setPreviousIndex] = useState<number | null>(null)
  const [preloadForIndex, setPreloadForIndex] = useState<number | null>(null)
  const [pageVisible, setPageVisible] = useState(() => document.visibilityState !== 'hidden')
  const [readyMediaKeys, setReadyMediaKeys] = useState<ReadonlySet<string>>(() => new Set())
  const [readyToneKeys, setReadyToneKeys] = useState<ReadonlySet<string>>(() => new Set())
  const initialReadyReportedRef = useRef(false)
  const committedBrightnessRef = useRef<boolean | null>(null)
  const pendingBrightnessRef = useRef<{ value: boolean | null; count: number }>({ value: null, count: 0 })
  const slideCount = preparedSlides.length
  const visibleActiveIndex = activeIndex % slideCount
  const activeSlide = preparedSlides[visibleActiveIndex]
  const activeReference = isMobile
    ? activeSlide.mobileSrc || activeSlide.desktopSrc
    : activeSlide.desktopSrc || activeSlide.mobileSrc || ''
  const activeMediaKey = `${activeSlide.id}|${activeReference}`

  const handleMediaReady = useCallback((mediaKey: string) => {
    setReadyMediaKeys((current) => {
      if (current.has(mediaKey)) return current
      const next = new Set(current)
      next.add(mediaKey)
      return next
    })
  }, [])

  const handleToneReady = useCallback((mediaKey: string) => {
    setReadyToneKeys((current) => {
      if (current.has(mediaKey)) return current
      const next = new Set(current)
      next.add(mediaKey)
      return next
    })
  }, [])

  useEffect(() => {
    if (
      initialReadyReportedRef.current
      || !readyMediaKeys.has(activeMediaKey)
      || !readyToneKeys.has(activeMediaKey)
    ) return
    initialReadyReportedRef.current = true
    onInitialMediaReady?.()
  }, [activeMediaKey, onInitialMediaReady, readyMediaKeys, readyToneKeys])

  const handleToneSample = useCallback((isBright: boolean, immediate: boolean) => {
    if (immediate || committedBrightnessRef.current === null) {
      pendingBrightnessRef.current = { value: null, count: 0 }
      if (committedBrightnessRef.current !== isBright) {
        committedBrightnessRef.current = isBright
        onActiveMediaBrightnessChange?.(isBright)
      }
      return
    }
    if (committedBrightnessRef.current === isBright) {
      pendingBrightnessRef.current = { value: null, count: 0 }
      return
    }
    const pending = pendingBrightnessRef.current
    pendingBrightnessRef.current = pending.value === isBright
      ? { value: isBright, count: pending.count + 1 }
      : { value: isBright, count: 1 }
    if (pendingBrightnessRef.current.count < 2) return
    committedBrightnessRef.current = isBright
    pendingBrightnessRef.current = { value: null, count: 0 }
    onActiveMediaBrightnessChange?.(isBright)
  }, [onActiveMediaBrightnessChange])

  useEffect(() => {
    const handleVisibilityChange = () => setPageVisible(document.visibilityState !== 'hidden')
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [])

  useEffect(() => {
    if (prefersReducedMotion || !pageVisible || slideCount < 2) return
    const currentIndex = visibleActiveIndex
    const timer = window.setTimeout(() => {
      setPreviousIndex(currentIndex)
      setActiveIndex((currentIndex + 1) % slideCount)
    }, slideDurationMs)
    return () => window.clearTimeout(timer)
  }, [pageVisible, prefersReducedMotion, slideCount, slideDurationMs, visibleActiveIndex])

  useEffect(() => {
    if (slideCount < 2 || prefersReducedMotion || !pageVisible) return
    const timer = window.setTimeout(() => setPreloadForIndex(visibleActiveIndex), 2500)
    return () => window.clearTimeout(timer)
  }, [pageVisible, prefersReducedMotion, slideCount, visibleActiveIndex])

  const nextIndex = (visibleActiveIndex + 1) % slideCount
  const canPlay = pageVisible && !prefersReducedMotion
  const transitionReady = previousIndex !== null && readyMediaKeys.has(activeMediaKey)
  const slideshowStyle = {
    '--hero-slide-duration': `${slideDurationMs}ms`,
  } as CSSProperties

  return (
    <div
      className={`hero-slideshow ${canPlay ? '' : 'is-motion-paused'} ${transitionReady ? 'is-transition-ready' : ''}`}
      data-slide-count={slideCount}
      style={slideshowStyle}
    >
      <img className="hero-slideshow__fallback" src={fallbackImage} alt="" aria-hidden="true" />
      {preparedSlides.map((slide, index) => {
        const active = index === visibleActiveIndex
        const previous = previousIndex !== null && index === previousIndex && !active
        const shouldLoad = active || previous || (preloadForIndex === visibleActiveIndex && slideCount > 1 && index === nextIndex)
        return (
          <HeroSlideLayer
            key={slide.id}
            slide={slide}
            fallbackImage={fallbackImage}
            isMobile={isMobile}
            slideDurationMs={slideDurationMs}
            initial={index === 0 && previousIndex === null}
            active={active}
            previous={previous}
            shouldLoad={shouldLoad}
            canPlay={canPlay}
            motionIndex={index}
            onToneSample={handleToneSample}
            onToneReady={handleToneReady}
            onMediaReady={handleMediaReady}
          />
        )
      })}

    </div>
  )
}
